import { Component, effect, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { DataSourceService } from '@app/finxone-web-frontend/app/lib/services/data-source-service/data-source.service';
import { HideConditionsService } from '@app/finxone-web-frontend/app/lib/services/hide-conditions-service/hide-conditions.service';
import { ResponsiveHideConditionsService } from '@app/finxone-web-frontend/app/lib/services/responsive-hide-conditions-service/responsive-hide-conditions.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  ProviderClass,
  TextAdditionalConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { WidgetTemplateHelperService } from '@app/finxone-web-frontend/app/lib/services/widget-template-helper/widget-template-helper.service';
import { TextComponent } from './text-component/text.component';

@Component({
  selector: 'finxone-web-frontend-text-widget',
  imports: [TextComponent],
  templateUrl: './text-widget.component.html',
  styleUrls: ['./text-widget.component.scss'],
})
export class TextWidgetComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public contentStyle: baseCssConfigStyle;
  public colorTextAlignStyle: baseCssConfigStyle;
  private attri: TextAdditionalConfig;
  private content = '';
  public renderedContent = '';
  public selectedTextStyleClass = '';

  private readonly widgetTemplateHelperService = inject(WidgetTemplateHelperService);

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<TextAdditionalConfig>,
    private matIconRegistry: MatIconRegistry,
    private injector: Injector,
    private zonePageUrlProvider: ZonePageUrlProvider,
    private alertHandlerService: AlertHandlerService,
    public pageConfig: PageConfigurationProvider,
    public zoneUrlProvider: ZonePageUrlProvider,
    private responsiveHideConditionsService: ResponsiveHideConditionsService,
    private dataSourceService: DataSourceService,
    private hideConditionsService: HideConditionsService,
  ) {
    effect(() => {
      const widgetAction = this.widgetTemplateHelperService.widgetAction();
      if (widgetAction) {
        this.widgetTemplateHelperService.checkWidgetTemplateAndDispatchStateUpdateIfNeeded(this.content);
      }
    });
  }

  ngOnInit(): void {
    if (this.dataIn.widgetConfig.attributes?.['usingResponsiveHideCondition']) {
      this.setupResponsiveHideConditionSubscription();
    }

    this.dataSourceService.dataSources$
      .pipe(takeUntil(this.destroy$), debounceTime(100), takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => {
        this.updateTemplate();
      });

    this.onLoadWidgetConfiguration();
  }

  private updateTemplate(): void {
    if (!this.content) return;

    const template = Handlebars.compile(this.content);

    buildHandleBarModel(
      this.injector.get(Store),
      this.content,
      this.injector.get(CountriesCurrenciesService),
      this.injector.get(IconCacheService),
      this.injector.get(AccountService),
      this.attri,
      this.isSkeletonPreview,
      this.injector.get(WorkflowFormsService),
      undefined,
      this.configService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
        this.hideConditionsService.currentModelToCheck.set(model);
      });
  }

  private onLoadWidgetConfiguration(): void {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
    }

    this.content = this.attri?.widgetProperties?.textContent;
    this.selectedTextStyleClass = this.attri?.widgetProperties?.textStyleClass ?? '';

    //move into helper folder for reusability
    Handlebars.registerHelper('icon', (iconName) => {
      return this.matIconRegistry.getNamedSvgIcon(iconName).pipe(take(1)).subscribe();
    });

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);

    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
    ]);

    this.updateTemplate();
  }

  private get isSkeletonPreview() {
    return (
      this.configService.getActiveConfigType().isWorkflow &&
      this.attri.widgetProperties?.['isSkeletonPreview']
    );
  }

  ngOnDestroy() {
    if (['transaction-summary', 'edit-transaction'].includes(this.zonePageUrlProvider.pageUrlInput)) {
      this.alertHandlerService.hideAlert();
    }
  }

  /**
   * Sets up a subscription to the widget's responsive hide condition and toggles visibility.
   *
   * If the condition is true, it hides the widget's wrapper
   * by adding the 'd-none' class to the element identified via getWidgetClasses(...).
   * If false, removes 'd-none' to show the widget.
   *
   * The subscription is lifecycle-managed using takeUntil(this.destroy$).
   *
   * @private
   * @returns void
   * @see getWidgetClasses
   */
  private setupResponsiveHideConditionSubscription(): void {
    this.responsiveHideConditionsService
      .setupHideCondition(this.dataIn.widgetConfig.attributes['responsiveHideCondition'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((shouldHide) => {
        // find the parent wrapper element for this widget present in main widget and show/hide it
        const widgetParentEl = document.getElementsByClassName(getWidgetClasses(this.dataIn.widgetConfig))[0];
        shouldHide ? widgetParentEl?.classList.add('d-none') : widgetParentEl?.classList.remove('d-none');
      });
  }
}
